import React, { useState, useEffect } from "react";
import { dataService } from "../../../Services/data/data.service";
import { Box } from '@mui/material';
import { makeStyles } from "@material-ui/core/styles";
import { DataListFilterTypes } from '../../../Config/constants';
import { Plus, Search } from 'lucide-react';
import ResetFiltersButton from "../../Filter/ResetFiltersButton";
import Button from "../../Shared/Buttons/Button";

import { useNavigate } from 'react-router-dom';
import TableList from "./Components/TableList";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../Shared/Select";
import TablePagination from "@mui/material/TablePagination";
import useFilterPersistence from '../../../hooks/useFilterPersistence';

function ListOverviewPage(props) {
    const navigate = useNavigate();
    const [firstLoad, setFirstLoad] = useState(0);
    const classes = useStyles();
    const [filterOn, setFilterOn] = useState(false);
    const [list, setList] = useState([]);
    const [originalList, setOriginalList] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState(1);
    const [companies, setCompanies] = useState([]);

    // ── Persistent filter state ────────────────────────────────────────────────
    const {
        filters,
        setFilterText,
        setFilter,
        resetFilters,
        getFilterValue,
        hasActiveFilters,
        page,
        setPage,
        pageSize,
        setPageSize,
    } = useFilterPersistence({
        pageKey: 'list-overview',
        filterSchema: {
            name:    DataListFilterTypes.NAME,
            company: DataListFilterTypes.COMPANY,
        },
        defaultFilters: [],
        defaultPage: 0,
        defaultPageSize: 25,
    });

    useEffect(() => {
        Promise.all([
            dataService.getLists(props.Login.userid),
            dataService.getAllCompaniesByUserId(props.Login.userid)
        ]).then(([listsResult, companiesResult]) => {
            setOriginalList(listsResult);
            setCompanies(companiesResult || []);
        });
    }, [props.Login.userid])

    // Re-apply filters whenever source data or active filters change (covers reset + URL restore)
    useEffect(() => {
        filterList(filters);
    }, [originalList, filters]); // eslint-disable-line react-hooks/exhaustive-deps

    if (firstLoad === 0) {
        setFirstLoad(1);
    }

    const filter = [
        { number: 1, column: "name", sortOrder: "ASC" },
        { number: 2, column: "name", sortOrder: "DESC" },
        { number: 3, column: "companyName", sortOrder: "ASC" },
        { number: 4, column: "companyName", sortOrder: "DESC" },
        { number: 5, column: "isMultiSelect", sortOrder: "ASC" },
        { number: 6, column: "isMultiSelect", sortOrder: "DESC" },
    ]

    const dynamicSorting = (property) => {
        var sortOrder = 1;
        if (property[0] === "-") {
            sortOrder = -1;
            property = property.substr(1);
        }
        let asc = false;
        /* SC: Filter now works both ways (ASC, DESC) */
        if (filter.find(x => x.number === selectedFilter).column === property) {
            if (filter.find(x => x.number === selectedFilter).sortOrder === 'ASC') {
                asc = false;
                setSelectedFilter(filter.find(x => x.column === property && x.sortOrder === 'DESC').number);
            } else {
                asc = true;
                setSelectedFilter(filter.find(x => x.column === property && x.sortOrder === 'ASC').number);
            }
        } else {
            asc = true;
            setSelectedFilter(filter.find(x => x.column === property && x.sortOrder === 'ASC').number);
        }
        if (asc === true) {
            return function (a, b) {
                //ASC A-Z
                if (typeof a[property] === 'boolean') {
                    var result = (a[property] === b[property]) ? 0 : a[property] ? 1 : -1;
                } else {
                    var result = (a[property].toLowerCase() > b[property].toLowerCase() ? 1 : -1);
                }
                return result * sortOrder;
            }
        } else {
            return function (a, b) {
                //DESC Z-A
                if (typeof a[property] === 'boolean') {
                    var result = (a[property] === b[property]) ? 0 : a[property] ? -1 : 1;
                } else {
                    var result = (a[property].toLowerCase() < b[property].toLowerCase() ? 1 : -1);
                }
                return result * sortOrder;
            }
        }
    }

    const handleSorting = (sortType) => {
        let tempMeasurementRows = [...list]
        setList(tempMeasurementRows.sort(dynamicSorting(sortType)));
    }

    const toggleFilter = () => {
        if (filterOn) {
            resetFilters();
        }
        setFilterOn(!filterOn);
    }

    const updateFilters = (type, event) => {
        // Debounced text sync — local state updates immediately
        setFilterText(type, event.target.value);
        filterList([{ type, filterValue: event.target.value }, ...filters.filter(f => f.type !== type)]);
    }

    const updateCompanyFilter = (selectedCompany) => {
        // Company filter is a dropdown — sync URL immediately
        const value = selectedCompany ? String(selectedCompany.id) : '';
        setFilter(DataListFilterTypes.COMPANY, value);
        filterList(
            value
                ? [{ type: DataListFilterTypes.COMPANY, filterValue: value }, ...filters.filter(f => f.type !== DataListFilterTypes.COMPANY)]
                : filters.filter(f => f.type !== DataListFilterTypes.COMPANY)
        );
    };

    const filterList = (thisFilters) => {
        let varFilteredlist = [...originalList].sort((a, b) =>
            a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
        );
        var i;
        for (i = 0; i < thisFilters.length; i++) {
            let filter = thisFilters[i]
            switch (filter.type) {
                case DataListFilterTypes.NAME:
                    varFilteredlist = varFilteredlist.filter(x => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()));
                    break;
                case DataListFilterTypes.COMPANY:
                    varFilteredlist = varFilteredlist.filter(x => x.companyId === parseInt(filter.filterValue) || filter.filterValue === "");
                    break;
                default:
                    varFilteredlist = varFilteredlist.filter(x => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()));
                    break;
            }
        }
        setList(varFilteredlist)
    }

    const navigateTo = (destination) => {
        navigate(destination);
    }

    // Utility function to get selected company
    const getSelectedCompany = () => {
        const companyId = getFilterValue(DataListFilterTypes.COMPANY);
        return companies.find((company) => company.id === parseInt(companyId)) || null;
    };

    // Utility function to get search value
    const getSearchValue = () => {
        return getFilterValue(DataListFilterTypes.NAME);
    };

    // Pagination handlers (delegated to the persistence hook)
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10)); // also resets page to 0
    };

    // Calculate paginated rows
    const paginatedRows = list.slice(page * pageSize, page * pageSize + pageSize);

    return (
        <React.Fragment>
            <Box className="page-container">
                <div className={classes.header}>
                    <h1>Manage your dropdowns</h1>
                </div>
                <div className={classes.tableManagementContainer}>
                    <div className={classes.tableManagementContainer__start}>
                        <div className={classes.searchContainer}>
                            <input placeholder="Search for dropdowns" className={classes.search__input} type="text" value={getFilterValue(DataListFilterTypes.NAME)} onChange={(e) => updateFilters(DataListFilterTypes.NAME, e)} />
                            <Search size={16} className={classes.inputIcon} />
                        </div>
                        <div className={classes.filterContainer}>
                            <Select value={getSelectedCompany()?.id} onChange={updateCompanyFilter} size="small">
                                <SelectTrigger>{getSelectedCompany()?.name || "All Companies"}</SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={null}>All Companies</SelectItem>
                                    {companies.map((company) => (
                                        <SelectItem key={company.id} value={company}>
                                            {company.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {/* Reset filters — visible whenever any filter is active */}
                        {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
                    </div>
                    <div className={classes.tableManagementContainer__end}>
                        <Button size="small" startIcon={<Plus />} onClick={() => navigateTo('/createdropdown')}>
                            Create dropdown
                        </Button>
                    </div>
                </div>
                <Box className={classes.container}>
                    <TableList 
                        toggleFilter={toggleFilter} 
                        filters={filters} 
                        handleSorting={handleSorting} 
                        filterOn={filterOn} 
                        filterList={filterList} 
                        updateFilters={updateFilters} 
                        rows={paginatedRows}
                        sortColumn={filter.find(x => x.number === selectedFilter)?.column}
                        sortOrder={filter.find(x => x.number === selectedFilter)?.sortOrder}
                        navigate={navigate}
                        searchValue={getSearchValue()}
                    />
                    <TablePagination
                        component="div"
                        count={list.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </Box>
            </Box>
        </React.Fragment>
    )
}

export default ListOverviewPage;

const useStyles = makeStyles({
    container: {
        // background: "#FFFFFF",
        borderRadius: "8px",
        // padding: "1.5rem",
        maxWidth: "100%",
        marginBottom: "24px",
    },

    header: {
        width: "100%",
        height: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "16px",
    },

    tableManagementContainer: {
        width: "100%",
        height: "fit-content",

        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        flexWrap: "wrap-reverse",

        marginBottom: "8px",
    },

    tableManagementContainer__start: {
        width: "fit-content",

        display: "flex",
        alignItens: "center",
        gap: "8px",
    },

    searchContainer: {
        width: "240px",
        position: 'relative',
    },

    filterContainer: {
        width: "200px",
    },

    search__input: {
        width: '100%',
        height: '32px',
        boxSizing: 'border-box',

        display: 'flex',
        alignItems: 'center',

        padding: '0px 8px',
        paddingRight: '32px',

        borderRadius: "4px",
        border: "1px solid #DCDCDC",

        fontSize: '14px',
        fontFamily: 'Lato, sans-serif',
        fontWeight: 'normal',
        color: "#0C0E29",

        backgroundColor: "#FFFFFF",

        "&:focus": {
            outline: "none",
            border: "1px solid var(--color-primary)",
        },
    },

    inputIcon: {
        color: "#0C0E29",
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: '8px',
    },

    tableManagementContainer__end: {
        width: "fit-content",

        display: "flex",
        alignItens: "center",
        gap: "8px",
    },

    tableManagementContainer__primaryIconButton: {
        height: "32px",
        padding: "0 12px",
        paddingLeft: "6px",

        border: "none",
        borderRadius: "4px",

        fontSize: "16px",
        color: "white",
        backgroundColor: "var(--color-primary)",

        display: "flex",
        gap: "4px",
        alignItems: "center",

        transition: "all 0.2s ease-in-out",

        "&:hover": {
            backgroundColor: "#a77d4f",
            color: "white",
            cursor: "pointer",
        }
    },


    iconButton__icon: {
        height: "20px",
        width: "20px",

        display: "block",
    },
});